import { BrowserRouter, Route, Routes } from "react-router";

import { FlagGuard } from "@/components/auth/flag-guard";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { RoleGuard } from "@/components/auth/role-guard";
import { TenantClaimGuard } from "@/components/auth/tenant-claim-guard";
import { ProtectedLayout } from "@/components/layouts/protected-layout";
import { LanguageGuard } from "@/components/routing/language-guard";
import { LanguageRedirect } from "@/components/routing/language-redirect";
import { ActivateApartmentPage } from "@/pages/(auth)/activate-apartment";
import { ForgotPasswordPage } from "@/pages/(auth)/forgot-password";
import { LoginPage } from "@/pages/(auth)/login";
import { PasswordResetConfirmPage } from "@/pages/(auth)/password-reset-confirm";
import { SetPasswordPage } from "@/pages/(auth)/set-password";
import { AlertsPage } from "@/pages/(protected)/alerts";
import { ApartmentsPage } from "@/pages/(protected)/apartments";
import { DashboardPage } from "@/pages/(protected)/dashboard";
import { DeploymentReportPage } from "@/pages/(protected)/deployment-report";
import { DesignSystemPage } from "@/pages/(protected)/design-system";
import { DetectedMetersPage } from "@/pages/(protected)/detected-meters";
import { DistrictDetailPage } from "@/pages/(protected)/district-detail";
import { DistrictsPage } from "@/pages/(protected)/districts";
import { ElectricityMetersPage } from "@/pages/(protected)/electricity-meters";
import { HeatMetersPage } from "@/pages/(protected)/heat-meters";
import { ManagersPage } from "@/pages/(protected)/managers";
import { MediaTransmittersPage } from "@/pages/(protected)/media-transmitters";
import { MeterDetailPage } from "@/pages/(protected)/meter-detail";
import { MeterKeysPage } from "@/pages/(protected)/meter-keys";
import { MqttMeterReadingsPage } from "@/pages/(protected)/mqtt-meter-readings";
import { MqttSourceDetailPage } from "@/pages/(protected)/mqtt-source-detail";
import { MyApartmentDetailPage } from "@/pages/(protected)/my-apartment-detail";
import { MyApartmentsPage } from "@/pages/(protected)/my-apartments";
import { ReadingDetailPage } from "@/pages/(protected)/reading-detail";
import { ReadingsPage } from "@/pages/(protected)/readings";
import { ResellersPage } from "@/pages/(protected)/resellers";
import { SettingsPage } from "@/pages/(protected)/settings";
import { SourcesPage } from "@/pages/(protected)/sources";
import { WaterMetersPage } from "@/pages/(protected)/water-meters";
import { NotFoundPage } from "@/pages/not-found";
import { hasDeploymentToolEnabled } from "@/types/auth";

export function App() {
	return (
		<BrowserRouter>
			<Routes>
				<Route path="/" element={<LanguageRedirect />} />

				<Route path="/:lang" element={<LanguageGuard />}>
					<Route path="login" element={<LoginPage />} />
					<Route path="forgot-password" element={<ForgotPasswordPage />} />
					<Route
						path="password/reset/confirm"
						element={<PasswordResetConfirmPage />}
					/>
					<Route path="password/set" element={<SetPasswordPage />} />

					<Route element={<ProtectedRoute />}>
						<Route element={<RoleGuard allowedRoles={["end_user"]} />}>
							<Route
								path="aktywuj-mieszkanie"
								element={<ActivateApartmentPage />}
							/>
							<Route
								path="activate-apartment"
								element={<ActivateApartmentPage />}
							/>
						</Route>

						<Route element={<TenantClaimGuard />}>
							<Route element={<ProtectedLayout />}>
								<Route index element={<DashboardPage />} />

								<Route path="ustawienia" element={<SettingsPage />} />
								<Route path="settings" element={<SettingsPage />} />

								<Route
									element={<RoleGuard allowedRoles={["district_manager"]} />}
								>
									<Route path="alerty" element={<AlertsPage />} />
									<Route path="alerts" element={<AlertsPage />} />
								</Route>

								<Route
									element={<RoleGuard allowedRoles={["reseller_manager"]} />}
								>
									<Route path="design-system" element={<DesignSystemPage />} />
								</Route>

								<Route
									element={<RoleGuard allowedRoles={["district_manager"]} />}
								>
									<Route path="wodomierze" element={<WaterMetersPage />} />
									<Route path="water-meters" element={<WaterMetersPage />} />
									<Route path="cieplomierze" element={<HeatMetersPage />} />
									<Route path="heat-meters" element={<HeatMetersPage />} />
									<Route
										path="liczniki-energii"
										element={<ElectricityMetersPage />}
									/>
									<Route
										path="electricity-meters"
										element={<ElectricityMetersPage />}
									/>
									<Route
										path="liczniki/:type/:sn"
										element={<MeterDetailPage />}
									/>
									<Route
										path="meters/:type/:sn"
										element={<MeterDetailPage />}
									/>
									<Route path="klucze" element={<MeterKeysPage />} />
									<Route path="keys" element={<MeterKeysPage />} />
									<Route path="lokale" element={<ApartmentsPage />} />
									<Route path="apartments" element={<ApartmentsPage />} />
								</Route>

								<Route
									element={
										<RoleGuard
											allowedRoles={["district_manager", "reseller"]}
										/>
									}
								>
									<Route path="osiedla" element={<DistrictsPage />} />
									<Route path="districts" element={<DistrictsPage />} />
									<Route
										path="osiedla/:name"
										element={<DistrictDetailPage />}
									/>
									<Route
										path="districts/:name"
										element={<DistrictDetailPage />}
									/>
								</Route>

								<Route
									element={
										<RoleGuard
											allowedRoles={["reseller", "reseller_manager"]}
										/>
									}
								>
									<Route path="zarzadcy" element={<ManagersPage />} />
									<Route path="managers" element={<ManagersPage />} />
								</Route>

								<Route
									element={
										<RoleGuard
											allowedRoles={[
												"reseller_manager",
												"reseller",
												"district_manager",
											]}
										/>
									}
								>
									<Route path="bramki" element={<SourcesPage />} />
									<Route path="gateways" element={<SourcesPage />} />
								</Route>

								<Route element={<RoleGuard allowedRoles={["reseller"]} />}>
									<Route path="bramki/:sn" element={<MqttSourceDetailPage />} />
									<Route
										path="gateways/:sn"
										element={<MqttSourceDetailPage />}
									/>
									<Route
										path="bramki/:sn/odczyty/:readingId/liczniki"
										element={<MqttMeterReadingsPage />}
									/>
									<Route
										path="gateways/:sn/readings/:readingId/meters"
										element={<MqttMeterReadingsPage />}
									/>
								</Route>

								<Route
									element={
										<RoleGuard
											allowedRoles={[
												"reseller",
												"reseller_manager",
												"district_manager",
											]}
										/>
									}
								>
									<Route path="odczyty" element={<ReadingsPage />} />
									<Route path="readings" element={<ReadingsPage />} />
								</Route>
								<Route
									element={
										<RoleGuard
											allowedRoles={[
												"reseller",
												"reseller_manager",
												"district_manager",
											]}
										/>
									}
								>
									<Route path="odczyty/:id" element={<ReadingDetailPage />} />
									<Route path="readings/:id" element={<ReadingDetailPage />} />
								</Route>

								<Route
									element={<RoleGuard allowedRoles={["reseller_manager"]} />}
								>
									<Route path="resellerzy" element={<ResellersPage />} />
									<Route path="resellers" element={<ResellersPage />} />
								</Route>

								<Route element={<RoleGuard allowedRoles={["end_user"]} />}>
									<Route
										path="moje-mieszkania"
										element={<MyApartmentsPage />}
									/>
									<Route path="my-apartments" element={<MyApartmentsPage />} />
									<Route
										path="moje-mieszkania/:id"
										element={<MyApartmentDetailPage />}
									/>
									<Route
										path="my-apartments/:id"
										element={<MyApartmentDetailPage />}
									/>
								</Route>

								<Route
									element={<FlagGuard predicate={hasDeploymentToolEnabled} />}
								>
									<Route
										path="nadajniki-mediow"
										element={<MediaTransmittersPage />}
									/>
									<Route
										path="media-transmitters"
										element={<MediaTransmittersPage />}
									/>
									<Route
										path="nadajniki-mediow/:sn/wykryte-liczniki"
										element={<DetectedMetersPage />}
									/>
									<Route
										path="media-transmitters/:sn/detected-meters"
										element={<DetectedMetersPage />}
									/>
									<Route
										path="raport-wdrozenia"
										element={<DeploymentReportPage />}
									/>
									<Route
										path="deployment-report"
										element={<DeploymentReportPage />}
									/>
								</Route>
							</Route>
						</Route>
					</Route>

					<Route path="*" element={<NotFoundPage />} />
				</Route>

				<Route path="*" element={<NotFoundPage />} />
			</Routes>
		</BrowserRouter>
	);
}

export default App;
